/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { isNone } from '@ember/utils';
import RouterService from '@ember/routing/router-service';
import ImportModel, { IMPORT_TYPES, LOADING_STATES } from 'cio/models/import';
import { importStateGte } from 'cio/helpers/import-state-gte';
import { computed } from '@ember/object';

interface NavbarArgs {
  import: ImportModel;
  isNew: boolean;
  type: string;
}

type GenerateNavArgs = {
  isNew: boolean;
  isDisabled: boolean;
  isMapAttributesChecked?: boolean;
  isDetailDirty?: boolean;
};

let MAP_ATTRIBUTES_SUCCESS_ROUTES = ['imports.import.setup.fields'];

let generatePeopleOrEventsImportNavigation = (
  record: ImportModel,
  { isNew, isDisabled, isMapAttributesChecked, isDetailDirty }: GenerateNavArgs,
) => {
  let { state } = record;

  return [
    {
      route: isNew ? 'imports.new' : 'imports.import.setup.index',
      model: record.id,
      state: 'start',
      label: 'Upload CSV',
      disabled: isDisabled && !isNew,
      condition: true,
    },
    {
      route: record.isEventsImport ? 'imports.import.setup.preview' : 'imports.import.setup.fields',
      model: record.id,
      state: 'preprocessed',
      label: record.isEventsImport ? 'Preview' : 'Map fields',
      disabled: isDisabled || !importStateGte([record.state, 'preprocessed']) || isDetailDirty,
      checked: record.isEventsImport || isMapAttributesChecked,
      condition: !isNew,
    },
    {
      route: 'imports.import.setup.review',
      model: record.id,
      state: 'validated',
      label: 'Review',
      disabled: isDisabled || state !== 'validated' || isDetailDirty,
      condition: !isNew,
    },
  ];
};

let generateSegmentImportNavigation = (record: ImportModel, { isNew, isDisabled }: GenerateNavArgs) => {
  let { state } = record;

  return [
    {
      route: isNew ? 'segments.segment.imports.csv.new' : 'segments.segment.imports.csv.setup.index',
      model: record.id,
      state: 'start',
      label: 'Upload CSV',
      disabled: isDisabled && !isNew,
      condition: true,
    },
    {
      route: 'segments.segment.imports.csv.setup.map',
      model: record.id,
      state: 'preprocessed',
      label: 'Map',
      disabled: isDisabled,
      condition: !isNew,
    },
    {
      route: 'segments.segment.imports.csv.setup.review',
      model: record.id,
      state: 'validated',
      label: 'Review',
      disabled: isDisabled || state !== 'validated',
      condition: !isNew,
    },
  ];
};

export default class Navbar extends Component<NavbarArgs> {
  @service router!: RouterService;

  get type() {
    return this.args.type ?? 'people';
  }

  get segmentImportNavigation() {
    return generateSegmentImportNavigation(this.args.import, {
      isNew: this.args.isNew,
      isDisabled: this.isDisabled,
      isMapAttributesChecked: this.isMapAttributesChecked,
    });
  }

  get peopleOrEventsImportNavigation() {
    return generatePeopleOrEventsImportNavigation(this.args.import, {
      isNew: this.args.isNew,
      isDisabled: this.isDisabled,
      isMapAttributesChecked: this.isMapAttributesChecked,
      isDetailDirty: this.isDetailDirty,
    });
  }

  get steps() {
    return this.type === IMPORT_TYPES.segment ? this.segmentImportNavigation : this.peopleOrEventsImportNavigation;
  }

  get isLoadingState() {
    return LOADING_STATES.includes(this.args.import.state);
  }

  get isDisabled() {
    return isNone(this.args.import.state) || this.isLoadingState || this.args.isNew;
  }

  get isMapAttributesChecked() {
    let record = this.args.import;
    if (record.get('isNew')) {
      return false;
    }

    let isMapAttributesSuccessRoute = MAP_ATTRIBUTES_SUCCESS_ROUTES.includes(this.router.currentRouteName);
    let { validateRules } = record;

    if (isNone(validateRules)) {
      return false;
    }

    let { mapAttributes, skipAttributes } = validateRules;
    let hasImportRules = mapAttributes && mapAttributes.length > 0 && skipAttributes && skipAttributes.length > 0;
    return record.canImport && (isMapAttributesSuccessRoute || hasImportRules);
  }

  @computed(
    'args.import.{name,description,importRules.clearEmptyCells}',
    'args.import.validateRules.{excludeExisting,onlyExisting,matchEmail}',
  )
  get isDetailDirty() {
    let record = this.args.import;
    let changedAttributes = record.changedAttributes();

    return (
      changedAttributes.name != null ||
      changedAttributes.description != null ||
      record.changedValidateRule('excludeExisting') ||
      record.changedValidateRule('onlyExisting') ||
      record.changedValidateRule('matchEmail') ||
      record.changedImportRule('clearEmptyCells')
    );
  }
}
